import {
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  SUPPORTED_LOCALES,
  formatLocaleFor,
  negotiateLocale,
  toLocale,
  type Locale,
} from '@recipes/contracts';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import de from './locales/de.json';
import en from './locales/en.json';

export type { Locale };

/**
 * German and English are part of the first load (the fallback has to be there anyway).
 * A further language pack registers a loader here and is fetched when it is chosen; the
 * service worker caches the chunk, so a language that was used once works offline.
 */
export const resources = { de: { translation: de }, en: { translation: en } } as const;
const LOADERS: Partial<Record<Locale, () => Promise<{ default: Record<string, unknown> }>>> = {};

const STORAGE_KEY = 'rm.locale';

function storedLocale(): Locale | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value ? toLocale(value) : null;
  } catch {
    return null;
  }
}

function browserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  return navigator.languages?.length ? navigator.languages : [navigator.language];
}

/** First visit: the browser's languages; afterwards what was chosen on this device. */
export function initialLocale(): Locale {
  return storedLocale() ?? negotiateLocale(browserLanguages(), SUPPORTED_LOCALES, DEFAULT_LOCALE);
}

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLocale(),
  // A missing key shows English, not German (decision D2).
  fallbackLng: FALLBACK_LOCALE,
  supportedLngs: [...SUPPORTED_LOCALES],
  load: 'languageOnly',
  interpolation: { escapeValue: false },
  returnNull: false,
});

function applyDocumentLanguage(lng: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lng;
  // The build emits one web app manifest per language; an installation made now gets this one.
  const manifest = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (manifest && lng in resources) manifest.href = `/manifest.${lng}.webmanifest`;
}
applyDocumentLanguage(i18n.language);
i18n.on('languageChanged', applyDocumentLanguage);

export default i18n;

export function currentLocale(): Locale {
  return toLocale(i18n.language, FALLBACK_LOCALE);
}

/** The locale for `Intl`: the UI language with the browser's region where it fits (`de-AT`). */
export function currentFormatLocale(): string {
  return formatLocaleFor(currentLocale(), browserLanguages());
}

/** Whether a language can be shown right now – false for a pack that is not on the device while offline. */
export function isLocaleAvailable(locale: Locale): boolean {
  return i18n.hasResourceBundle(locale, 'translation') || Boolean(LOADERS[locale]);
}

/**
 * Switches the UI language in place – no reload, forms keep their content – and remembers it
 * on this device. Throws when the language pack cannot be loaded (offline, never used before).
 */
export async function applyLocale(locale: Locale): Promise<void> {
  if (!i18n.hasResourceBundle(locale, 'translation')) {
    const loader = LOADERS[locale];
    if (!loader) throw new Error(`No language pack for ${locale}`);
    const pack = await loader();
    i18n.addResourceBundle(locale, 'translation', pack.default, true, true);
  }
  if (currentLocale() !== locale) await i18n.changeLanguage(locale);
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // private mode: the choice lasts for this page only
  }
}
